import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { IonApp, IonProgressBar, IonRouterOutlet } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { AuthService } from './core/auth/auth.service';
import { DeepLinkService } from './core/deep-link.service';
import { ThemeService } from './core/theme/theme.service';
import { LoadingService } from './core/loading/loading.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [IonApp, IonProgressBar, IonRouterOutlet],
  template: `
    <ion-app>
      @if (loading.isLoading()) {
        <ion-progress-bar
          type="indeterminate"
          class="global-progress-bar"
          data-testid="global-loading-bar"
        ></ion-progress-bar>
      }
      <ion-router-outlet></ion-router-outlet>
    </ion-app>
  `,
  styles: [
    `
      .global-progress-bar {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 3px;
        z-index: 99999;
        --progress-background: var(--ln-accent, #9dff00);
        --color: var(--ln-accent, #9dff00);
        --background: transparent;
        --buffer-background: transparent;
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly deepLink = inject(DeepLinkService);
  private readonly _theme = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly loading = inject(LoadingService);

  async ngOnInit(): Promise<void> {
    this.initRouterLoadingListener();
    // Silent refresh attempt: if the user has a valid lance.refresh cookie (PWA)
    // or stored refresh in @capacitor/preferences (native), they stay logged in.
    await this.auth.bootstrap();
    await this.deepLink.init();
    await this.configureNativeUI();
  }

  private initRouterLoadingListener(): void {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.loading.setNavigating(true);
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          this.loading.setNavigating(false);
        }
      });
  }

  private async configureNativeUI(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    this._theme.syncNativeStatusBar();
    try {
      await SplashScreen.hide();
    } catch {
      // Splash already hidden or unavailable.
    }
  }
}
